import { useContext, useRef, type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';

import { FEED_FRAME } from '../cardFrame';
import { BUTTON_TEXT_CLASS } from '../registry/tokens';

import { FeedCompactRow } from './FeedCompactRow';
import { FeedDensityContext, isDenseFeed } from './feedDensity';
import { FeedFrameHeader } from './FeedFrameHeader';
import { FeedMorph } from './FeedMorph';
import { FeedRow } from './FeedRow';
import { useFeedFocusHandoff } from './useFeedFocusHandoff';

import type { FeedPillIcon, FeedRowView } from './feedRowView';

export type FeedRowCardAction = {
  label: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  loading?: boolean;
};

type FeedRowCardFrame = {
  open: boolean;
  onToggle: () => void;
  title: string;
  label: string;
  headerActions?: ReactNode;
  body?: ReactNode;
  object?: ReactNode;
  actions?: readonly FeedRowCardAction[];
  note?: string;
};

type FeedRowCardProps = {
  title: string;
  age: string | null;
  icon: ReactNode;
  appName: string;
  label: string;
  labelAccessory?: ReactNode;
  description?: string;
  stake?: FeedRowView['stake'];
  pill?: {
    label: string;
    runningLabel: string;
    icon: FeedPillIcon;
    onPress: () => void;
  };
  running?: boolean;
  onMarkDone?: () => void;
  hoverActions?: ReactNode;
  frame?: FeedRowCardFrame;
};

const ignoreUrl = () => {};

export const FeedRowCard = ({
  title,
  age,
  icon,
  appName,
  label,
  labelAccessory,
  description,
  stake = null,
  pill,
  running = false,
  onMarkDone,
  hoverActions,
  frame,
}: FeedRowCardProps) => {
  const open = frame?.open ?? false;
  const density = useContext(FeedDensityContext);
  const frameRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  useFeedFocusHandoff(open, true, { frameRef, rowRef });
  const view: FeedRowView = {
    title,
    verb: null,
    age,
    stake,
    pill: pill
      ? {
          press: { kind: 'run', key: 'primary' },
          label: pill.label,
          runningLabel: pill.runningLabel,
          icon: pill.icon,
        }
      : null,
    running,
    failed: false,
    outcome: null,
    openUrl: null,
  };
  const rowProps = {
    view,
    icon,
    appName,
    label,
    labelAccessory,
    onOpen: frame?.onToggle,
    onPress: () => pill?.onPress(),
    onOpenUrl: ignoreUrl,
    onMarkDone,
    hoverActions,
  };

  return (
    <FeedMorph open={open}>
      {frame?.open ? (
        <div
          ref={frameRef}
          tabIndex={-1}
          data-feed-own-keys
          className="outline-none"
          onKeyDown={(event) => {
            if (
              event.target !== event.currentTarget ||
              event.metaKey ||
              event.ctrlKey ||
              event.altKey ||
              event.shiftKey ||
              (event.key !== 'Enter' && event.key !== ' ')
            )
              return;
            event.preventDefault();
            frame.onToggle();
          }}
        >
          <div role="article" aria-label={frame.title} className={FEED_FRAME}>
            <FeedFrameHeader
              view={view}
              title={frame.title}
              label={frame.label}
              icon={icon}
              onClose={frame.onToggle}
              actions={frame.headerActions}
            />
            {frame.body ? (
              <div>
                <div>
                  <div data-frame-text>{frame.body}</div>
                </div>
              </div>
            ) : null}
            {frame.object ? (
              <div>
                <div className="flex flex-col gap-[calc(12*var(--ac-u))]">
                  <div
                    data-card-object
                    className="text-[length:var(--ac-body)] leading-[var(--ac-body-lh)] text-foreground"
                  >
                    {frame.object}
                  </div>
                  <div className="flex flex-wrap items-center gap-[calc(8*var(--ac-u))]">
                    {frame.actions?.map((action, index) => (
                      <Button
                        key={index}
                        variant={action.primary ? 'default' : 'outline'}
                        size="sm"
                        className={BUTTON_TEXT_CLASS}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        isLoading={action.loading}
                      >
                        {action.label}
                      </Button>
                    ))}
                    {frame.note ? (
                      <span className="ml-auto text-[length:calc(12.5*var(--ac-u))] leading-[calc(18*var(--ac-u))] text-foreground-muted">
                        {frame.note}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div ref={rowRef} data-feed-own-keys>
          {isDenseFeed(density) ? (
            <FeedCompactRow {...rowProps} />
          ) : (
            <FeedRow {...rowProps} description={description} />
          )}
        </div>
      )}
    </FeedMorph>
  );
};
